import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { examActions, notificationActions, examAssignmentActions, userActions, classActions, studentActions } from '../../redux/actions';
import { AppDispatch, RootState } from '../../store';
import ExamFormModal from './ExamFormModal';
import ExamPreviewModal from './ExamPreviewModal';

import { examService, examAssignmentService } from '../../services';
import { getImageUrl } from '../../utils/imageHelper';
import {
  Container,
  Header,
  Title,
  CreateButton,
  FilterBar,
  CardContent,
  RowCommon,
  RowPublished,
  CardHeader,
  TitleRow,
  SearchInput,
  FilterSelect,
  CardsGrid,
  ExamCard,
  CardHeaderContent,
  ExamTitle,
  ClassInfo,
  StatusBadge,
  ExamDescription,
  ExamMeta,
  MetaItem,
  MetaLabel,
  DifficultyBadge,
  ShareLinkContainer,
  CopyButton,
  InfoColumn,
  MetaColumn,
  MetaRow,
  CardActions,
  ActionButton,
  EmptyState,
  LoadingCard,
  Pagination,
  PageButton,
  StatusTagGroup,
  StatusTag,
} from './ExamsManage.styled';

interface Exam {
  id: number;
  title: string;
  description?: string;
  duration: number;
  applicable_classes?: string;
  total_marks?: number;
  difficulty_level?: string;
  avg_difficulty?: string;
  status: 'draft' | 'published';
  total_questions?: number;
  share_link?: string;
  created_by?: number;
  created_by_name?: string;
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalBox = styled.div`
  width: min(1400px, 96vw);
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const CloseBtn = styled.button`
  border: none;
  background: #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 700;
`;

const SubmissionsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  th, td { padding: 8px; border-bottom: 1px solid #e2e8f0; text-align: left; }
  th { background: #f8fafc; font-weight: 700; }
`;

const AnswerCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  background: #f8fafc;
  margin-bottom: 10px;
`;

const Pill = styled.span<{variant: 'success' | 'danger'}>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => p.variant === 'success' ? '#166534' : '#991b1b'};
  background: ${(p) => p.variant === 'success' ? '#dcfce7' : '#fee2e2'};
`;

const TagButton = styled.button<{ active?: boolean }>`
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.active ? '#1d4ed8' : '#cbd5e1')};
  background: ${(p) => (p.active ? '#e0e7ff' : '#fff')};
  color: #0f172a;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 13px;
  &:hover { border-color: #2563eb; box-shadow: 0 4px 10px rgba(37, 99, 235, 0.12); }
`;

const ModalContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1.7fr;
  gap: 14px;
  flex: 1;
  min-height: 0;
`;

const Panel = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const PanelHeader = styled.div`
  font-weight: 700;
  color: #0f172a;
  padding: 6px 4px 10px;
`;

const SubmissionSelector = styled.select`
  padding: 6px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 13px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const PanelBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-right: 4px;
`;

const AssignModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 70vh;
  overflow-y: auto;
`;

const StudentListContainer = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  max-height: 400px;
  overflow-y: auto;
  background: #f8fafc;
`;

const StudentCheckbox = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f1f5f9;
  }

  input[type="checkbox"] {
    margin-right: 10px;
    cursor: pointer;
    width: 16px;
    height: 16px;
  }

  label {
    flex: 1;
    cursor: pointer;
    margin: 0;
  }
`;

const AssignButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const AssignButton = styled.button<{variant?: 'primary' | 'secondary' | 'danger'}>`
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  ${(p) => {
    if (p.variant === 'danger') {
      return `
        background: #fee2e2;
        color: #991b1b;
        &:hover { background: #fecaca; }
      `;
    }
    if (p.variant === 'secondary') {
      return `
        background: #e2e8f0;
        color: #1e293b;
        &:hover { background: #cbd5e1; }
      `;
    }
    return `
      background: #3b82f6;
      color: white;
      &:hover { background: #2563eb; }
    `;
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AssignedStudentsList = styled.div`
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  padding: 12px;

  .list-header {
    font-weight: 600;
    margin-bottom: 8px;
    color: #166534;
  }

  .student-tag {
    display: inline-block;
    background: #86efac;
    color: #166534;
    padding: 4px 8px;
    border-radius: 4px;
    margin-right: 6px;
    margin-bottom: 6px;
    font-size: 13px;
    position: relative;

    .remove-btn {
      margin-left: 6px;
      cursor: pointer;
      font-weight: bold;
      color: #166534;

      &:hover {
        color: #991b1b;
      }
    }
  }
`;

const AssignSearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 12px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const AssignToolbar = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
`;

const AssignSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const AssignIconButton = styled.button`
  padding: 8px 12px;
  border: 1px solid #3b82f6;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 13px;

  &:hover {
    background: #bfdbfe;
    border-color: #1e40af;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const formatAnswer = (ans: any) => {
  if (ans === null || ans === undefined) return '—';
  if (typeof ans === 'string') return ans;
  if (Array.isArray(ans)) return ans.join(', ');
  try {
    return JSON.stringify(ans);
  } catch {
    return String(ans);
  }
};

const shouldHighlightWordDiff = (typeId?: number) => [2, 3, 4, 5, 11, 13].includes(Number(typeId));

const splitCorrectAnswerCandidates = (answerText: string) =>
  answerText
    .split('|')
    .map((a) => a.trim())
    .filter((a) => a && a !== '—');

const highlightMismatchedWords = (studentText: string, correctCandidates: string[]): React.ReactNode => {
  if (!studentText || studentText === '—' || correctCandidates.length === 0) return studentText;

  const normalizeToken = (token: string) => token.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '').toLowerCase();

  const tokenizeWithSpaces = (text: string) =>
    text
      .split(/(\s+)/)
      .filter((part) => part !== '')
      .map((part) => ({
        raw: part,
        isSpace: part.trim() === '',
        norm: part.trim() === '' ? '' : normalizeToken(part),
      }));

  const buildFreqMap = (tokens: { norm: string; isSpace: boolean }[]) =>
    tokens.reduce<Record<string, number>>((acc, t) => {
      if (!t.isSpace && t.norm) {
        acc[t.norm] = (acc[t.norm] || 0) + 1;
      }
      return acc;
    }, {});

  const scoreCandidate = (studentTokens: { norm: string; isSpace: boolean }[], candidateTokens: { norm: string; isSpace: boolean }[]) => {
    const freq = buildFreqMap(candidateTokens);
    let matches = 0;
    studentTokens.forEach((t) => {
      if (!t.isSpace && t.norm && freq[t.norm]) {
        matches += 1;
        freq[t.norm] -= 1;
      }
    });
    return matches;
  };

  const studentTokens = tokenizeWithSpaces(studentText);
  const candidatesTokens = correctCandidates.map(tokenizeWithSpaces);
  const bestCandidateIndex = candidatesTokens.reduce((bestIdx, tokens, idx) => {
    const bestScore = scoreCandidate(studentTokens, candidatesTokens[bestIdx] || []);
    const score = scoreCandidate(studentTokens, tokens);
    return score > bestScore ? idx : bestIdx;
  }, 0);

  const bestTokens = candidatesTokens[bestCandidateIndex] || [];
  const freq = buildFreqMap(bestTokens);

  return (
    <span>
      {studentTokens.map((t, idx) => {
        if (t.isSpace || !t.norm) {
          return <React.Fragment key={idx}>{t.raw}</React.Fragment>;
        }
        const isMatch = freq[t.norm] > 0;
        if (isMatch) {
          freq[t.norm] -= 1;
          return <React.Fragment key={idx}>{t.raw}</React.Fragment>;
        }
        return (
          <span key={idx} style={{ color: '#dc2626', fontWeight: 600 }}>
            {t.raw}
          </span>
        );
      })}
    </span>
  );
};

const formatDateTime = (ts?: string) => {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString('vi-VN', {
      hour12: false,
      timeZone: 'Asia/Ho_Chi_Minh',
    });
  } catch {
    return ts;
  }
};

const ExamsManage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { examsList, pagination, loading } = useSelector((state: RootState) => state.exams);
  const { items: users = [] } = useSelector((state: RootState) => (state as any).users) || {};
  const currentUser = useSelector((state: RootState) => (state as any).authentication?.userInfo);
  const classes = useSelector((state: RootState) => (state as any).classes?.classesList || []);
  const allStudentsData = useSelector((state: RootState) => (state as any).students?.studentsList || []);
  const students = useSelector((state: RootState) => (state as any).users?.items || []).filter((u: any) => u?.role === 2);
  const userNameMap = useMemo(() => {
    const map: Record<number, string> = {};
    (users || []).forEach((u: any) => {
      const key = Number(u?.id);
      if (!Number.isNaN(key)) {
        map[key] = u.userName || u.name || u.fullName || u.full_name || '';
      }
    });
    return map;
  }, [users]);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [creatorFilter, setCreatorFilter] = useState('');
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedExam, setSelectedExam] = useState<any>(null);
  const [searchDebounce, setSearchDebounce] = useState('');
  const [submissionsModalOpen, setSubmissionsModalOpen] = useState(false);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [submissionsError, setSubmissionsError] = useState<string | null>(null);
  const [submissionsExam, setSubmissionsExam] = useState<any>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);
  const [selectedSubmissionForDetail, setSelectedSubmissionForDetail] = useState<any>(null);
  const [submissionSearch, setSubmissionSearch] = useState('');
  const [answerFilter, setAnswerFilter] = useState<'all' | 'correct' | 'wrong'>('all');
  const [showExplanationMap, setShowExplanationMap] = useState<Record<number, boolean>>({});
  const [submissionCounts, setSubmissionCounts] = useState<Record<number, number>>({});
  const [fetchingCounts, setFetchingCounts] = useState(false);
  
  // Assign modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignToExam, setAssignToExam] = useState<any>(null);
  const [selectedStudentsForAssign, setSelectedStudentsForAssign] = useState<number[]>([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignedStudents, setAssignedStudents] = useState<any[]>([]);
  const [assignSearchTerm, setAssignSearchTerm] = useState('');
  const [selectedClassForAssign, setSelectedClassForAssign] = useState<number | null>(null);
  const detailPanelBodyRef = useRef<HTMLDivElement>(null);
  
  // Ranking tab state
  const [submissionTab, setSubmissionTab] = useState<'list' | 'ranking'>('list');
  const [rankingData, setRankingData] = useState<any[]>([]);

  // All-submissions modal state
  const [allSubsModalOpen, setAllSubsModalOpen] = useState(false);
  const [allSubsData, setAllSubsData] = useState<any[]>([]);
  const [allSubsLoading, setAllSubsLoading] = useState(false);
  const [allSubsClassFilter, setAllSubsClassFilter] = useState('');
  const [allSubsSearch, setAllSubsSearch] = useState('');

  // Helper: Get student info with class
  const getStudentInfo = (userId: number) => {
    const user = students.find((u: any) => u.id === userId);
    if (!user) return null;
    const student = allStudentsData.find((s: any) => s.id === user.studentId);
    const studentClass = student ? classes.find((c: any) => c.id === student.classId) : null;
    return { user, student, studentClass };
  };

  // Filter students by search term
  const filteredStudentsForAssign = useMemo(() => {
    let result = students;
    
    // Filter by search term (name or userName)
    if (assignSearchTerm.trim()) {
      const keyword = assignSearchTerm.toLowerCase();
      result = result.filter((u: any) => {
        const userNameMatch = (u.userName || '').toLowerCase().includes(keyword);
        const info = getStudentInfo(u.id);
        const studentNameMatch = info?.student?.name ? info.student.name.toLowerCase().includes(keyword) : false;
        return userNameMatch || studentNameMatch;
      });
    }

    // Filter by selected class
    if (selectedClassForAssign) {
      result = result.filter((u: any) => {
        const info = getStudentInfo(u.id);
        return info?.student?.classId === selectedClassForAssign;
      });
    }

    return result;
  }, [students, assignSearchTerm, selectedClassForAssign, allStudentsData, classes]);

  // Get unique classes from students
  const availableClasses = useMemo(() => {
    const classIds = new Set<number>();
    allStudentsData.forEach((s: any) => {
      if (s.classId) classIds.add(s.classId);
    });
    return Array.from(classIds)
      .map(id => classes.find((c: any) => c.id === id))
      .filter(Boolean)
      .sort((a: any, b: any) => (a?.name || '').localeCompare(b?.name || ''));
  }, [allStudentsData, classes]);

  // Function to get student name and class for a submission
  const getSubmissionNameAndClass = (submission: any) => {
    // submission.student_id là user id trong bảng users
    const userId = submission?.student_id;
    
    if (!userId) {
      return { name: submission?.participant_name || 'Ẩn danh', className: 'Tự Do' };
    }
    
    // Tìm user từ user id
    const user = users.find((u: any) => u.id === userId);
    console.log(`User with id ${userId}:`, user);
    
    if (!user || !user.studentId) {
      return { name: submission?.participant_name || 'Ẩn danh', className: 'Tự Do' };
    }
    
    // Tìm student từ user.studentId
    const studentInfo = allStudentsData.find((s: any) => s.id === user.studentId);
    console.log(`Student with studentId ${user.studentId}:`, studentInfo);
    
    // Lấy tên học sinh
    const name = studentInfo?.name || submission?.participant_name || 'Ẩn danh';
    
    // Lấy tên lớp từ student.classId
    let className = 'Tự Do';
    if (studentInfo?.classId) {
      const classData = classes.find((c: any) => c.id === studentInfo.classId);
      className = classData?.name || 'Tự Do';
    }
    
    console.log(`Final result - name: ${name}, className: ${className}`);
    return { name, className };
  };

  // Group submissions by student_id and keep only latest per student
  const groupedSubmissions = useMemo(() => {
    const grouped: Record<number, any[]> = {};
    
    // Group all submissions by student_id
    submissions.forEach((sub) => {
      const studentId = sub.student_id;
      if (!grouped[studentId]) {
        grouped[studentId] = [];
      }
      grouped[studentId].push(sub);
    });
    
    // For each student, sort by finished_at (newest first) and keep all
    const result: any[] = [];
    Object.values(grouped).forEach((studentSubs) => {
      const sorted = studentSubs.sort((a, b) => {
        const timeA = new Date(a.finished_at || 0).getTime();
        const timeB = new Date(b.finished_at || 0).getTime();
        return timeB - timeA; // Newest first
      });
      // Add the latest submission to result, but store all submissions
      if (sorted.length > 0) {
        result.push({
          ...sorted[0],
          _allSubmissions: sorted, // Store all attempts for this student
          _submissionCount: sorted.length,
        });
      }
    });
    
    return result;
  }, [submissions]);

  // Filter grouped submissions by search keyword (name or class)
  const filteredSubmissions = useMemo(() => {
    const keyword = submissionSearch.trim().toLowerCase();
    if (!keyword) return groupedSubmissions;
    return groupedSubmissions.filter((s) => {
      const name = getSubmissionNameAndClass(s).name || (s.participant_name || 'Ẩn danh');
      const className = getSubmissionNameAndClass(s).className || '';
      return name.toLowerCase().includes(keyword) || className.toLowerCase().includes(keyword);
    });
  }, [submissionSearch, groupedSubmissions]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    // Seed submission count map if API returns counts
    if (examsList && examsList.length > 0) {
      const nextCounts: Record<number, number> = { ...submissionCounts };
      examsList.forEach((exam: any) => {
        const id = exam?.id;
        if (id === undefined || id === null) return;
        const count = exam.submission_count ?? exam.submissions_count ?? exam.submissions?.length;
        if (typeof count === 'number' && !Number.isNaN(count)) {
          nextCounts[id] = count;
        }
      });
      setSubmissionCounts(nextCounts);
    }
  }, [examsList]);

  useEffect(() => {
    fetchExams();
  }, [currentPage, searchDebounce, statusFilter, difficultyFilter, creatorFilter]);

  // Fetch users IMMEDIATELY on mount
  useEffect(() => {
    const fetchData = async () => {
      // Fetch users to resolve creator names
      await dispatch(userActions.getAllTeachers());
      // Fetch students for assign modal
      await dispatch(userActions.getAllStudents());
      // Fetch classes for assign modal
      try {
        await dispatch((classActions.getAll as any)());
      } catch (e) {
        // Ignore error if getAll doesn't exist
      }
      // Fetch students data for detailed info
      try {
        await dispatch((studentActions.getAll as any)());
      } catch (e) {
        // Ignore error if getAll doesn't exist
      }
      // Mark users as loaded
      setUsersLoaded(true);
    };
    fetchData();
  }, [dispatch]);

  // Set default creator filter to current user when both loaded
  useEffect(() => {
    if (usersLoaded && currentUser?.id && !creatorFilter) {
      console.log('Setting creatorFilter to:', currentUser.id);
      setCreatorFilter(currentUser.id.toString());
    }
  }, [usersLoaded, currentUser, creatorFilter]);

  // Load already assigned students when assign modal is opened
  useEffect(() => {
    if (showAssignModal && assignToExam?.id) {
      loadAssignedStudents();
      // Reset filters when opening modal
      setAssignSearchTerm('');
      setSelectedClassForAssign(null);
    }
  }, [showAssignModal, assignToExam]);

  const fetchSubmissionCounts = async (exams: any[]) => {
    if (!exams || exams.length === 0 || fetchingCounts) return;
    const publishedIds = exams
      .filter((exam: any) => exam?.status === 'published')
      .map((exam: any) => exam.id)
      .filter((id: any) => id !== undefined && id !== null && submissionCounts[id] === undefined);
    if (publishedIds.length === 0) return;

    setFetchingCounts(true);
    try {
      const results = await Promise.allSettled(
        publishedIds.map((id: number) => examService.getSubmissions(id))
      );

      const nextCounts: Record<number, number> = {};
      results.forEach((res, idx) => {
        if (res.status === 'fulfilled') {
          const list = (res.value as any)?.data ?? (res.value as any);
          if (Array.isArray(list)) {
            nextCounts[publishedIds[idx]] = list.length;
          }
        }
      });

      if (Object.keys(nextCounts).length > 0) {
        setSubmissionCounts((prev) => ({ ...prev, ...nextCounts }));
      }
    } catch (err: any) {
      dispatch(notificationActions.addNotification('Không tải được số bài nộp', 'DANGER'));
    } finally {
      setFetchingCounts(false);
    }
  };

  const fetchExams = async () => {
    try {
      const res: any = await dispatch(examActions.getAllPaginated({
        page: currentPage,
        limit: 20,
        search: searchDebounce,
        status: statusFilter,
        difficulty: difficultyFilter,
        created_by: creatorFilter,
      }));
      const list = res?.data || res?.payload?.data || examsList;
      fetchSubmissionCounts(list);
    } catch (err) {
      // notification handled inside action; avoid unhandled rejection
    }
  };

  const handleCreateNew = () => {
    navigate('/exams-manage/create');
  };

  const handleEdit = (exam: any, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Không cho phép sửa đề thi đã published
    if (exam.status === 'published') {
      if (window.confirm('⚠️ Đề thi đã xuất bản không thể sửa!\n\n💡 Bạn có muốn tạo bản sao (draft) để chỉnh sửa không?')) {
        handleDuplicate(exam.id, e);
      }
      return;
    }
    
    setSelectedExam(exam);
    setShowFormModal(true);
  };

  const handleDelete = async (examId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (window.confirm('Bạn có chắc chắn muốn xóa đề thi này?')) {
      await dispatch(examActions.deleteExam(examId));
      fetchExams();
    }
  };

  const handleDuplicate = async (examId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    await dispatch(examActions.duplicate(examId));
    fetchExams();
  };

  const handleViewSubmissions = async (exam: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSubmissionsModalOpen(true);
    setSubmissionsExam(exam);
    setSubmissions([]);
    setSubmissionsError(null);
    setSelectedSubmission(null);
    setSelectedSubmissionForDetail(null);
    setSubmissionsLoading(true);
    try {
      // Fetch submissions, students, and classes data in parallel
      const [submissionsRes] = await Promise.all([
        examService.getSubmissions(exam.id),
        dispatch(userActions.getAllStudents() as any),
        dispatch(classActions.getAll() as any),
        dispatch(studentActions.getAll() as any),
      ]);
      
      setSubmissions(submissionsRes.data || []);
      if (Array.isArray(submissionsRes.data)) {
        setSubmissionCounts(prev => ({ ...prev, [exam.id]: submissionsRes.data.length }));
      }
    } catch (err: any) {
      const msg = err?.toString() || 'Không tải được bài làm';
      setSubmissionsError(msg);
      dispatch(notificationActions.addNotification(msg, 'DANGER'));
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const handleOpenAssignModal = (exam: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setAssignToExam(exam);
    setSelectedStudentsForAssign([]);
    setShowAssignModal(true);
  };

  const loadAssignedStudents = async () => {
    if (!assignToExam) return;
    setAssignLoading(true);
    try {
      const res = await examAssignmentService.getExamAssignedStudents(assignToExam.id);
      const assigned = res.data || [];
      setAssignedStudents(assigned);
      setSelectedStudentsForAssign(assigned.map((s: any) => s.user_id));
    } catch (err: any) {
      dispatch(notificationActions.addNotification('Không tải được danh sách học sinh đã gán', 'DANGER'));
    } finally {
      setAssignLoading(false);
    }
  };

  const handleSelectStudentForAssign = (studentId: number, checked: boolean) => {
    if (checked) {
      setSelectedStudentsForAssign(prev => Array.from(new Set([...prev, studentId])));
    } else {
      setSelectedStudentsForAssign(prev => prev.filter(id => id !== studentId));
    }
  };

  const handleSubmitAssign = async () => {
    if (selectedStudentsForAssign.length === 0) {
      dispatch(notificationActions.addNotification('Vui lòng chọn ít nhất một học sinh', 'INFO'));
      return;
    }

    // Only assign to students who are newly selected but not already assigned
    const studentToAssignIds = selectedStudentsForAssign.filter(
      (studentId) => !assignedStudents.some((assignment: any) => assignment.user_id === studentId)
    );

    if (studentToAssignIds.length === 0) {
      dispatch(notificationActions.addNotification('Tất cả học sinh đã được gán đề thi rồi', 'INFO'));
      return;
    }

    setAssignLoading(true);
    try {
      await examAssignmentService.assignExamToStudents(assignToExam.id, studentToAssignIds);
      dispatch(notificationActions.addNotification(`Đã gán đề thi cho ${studentToAssignIds.length} học sinh`, 'SUCCESS'));
      
      // Reload assigned students
      await loadAssignedStudents();
      setSelectedStudentsForAssign([]);
      
      // Close modal after successful assignment
      setShowAssignModal(false);
    } catch (err: any) {
      const msg = err?.toString() || 'Lỗi khi gán đề thi';
      dispatch(notificationActions.addNotification(msg, 'DANGER'));
    } finally {
      setAssignLoading(false);
    }
  };

  const handleRemoveAssignedStudent = async (assignmentId: number) => {
    if (!window.confirm('Bạn có chắc chắn muốn gỡ gán đề thi này?')) return;

    try {
      await examAssignmentService.deleteAssignment(assignmentId);
      dispatch(notificationActions.addNotification('Đã gỡ gán đề thi', 'SUCCESS'));
      await loadAssignedStudents();
    } catch (err: any) {
      const msg = err?.toString() || 'Lỗi khi gỡ gán đề thi';
      dispatch(notificationActions.addNotification(msg, 'DANGER'));
    }
  };

  // Handle ranking
  const handleOpenRanking = () => {
    // Get best submission for each student
    const bestSubmissions: Record<number, any> = {};
    
    submissions.forEach((sub: any) => {
      const studentId = sub.student_id;
      const score = sub.total_score || 0;
      
      if (!bestSubmissions[studentId] || score > bestSubmissions[studentId].total_score) {
        bestSubmissions[studentId] = sub;
      } else if (score === bestSubmissions[studentId].total_score) {
        // If same score, choose the one with faster completion time
        const currentTime = new Date(sub.finished_at || 0).getTime() - new Date(sub.started_at || 0).getTime();
        const bestTime = new Date(bestSubmissions[studentId].finished_at || 0).getTime() - new Date(bestSubmissions[studentId].started_at || 0).getTime();
        if (currentTime < bestTime) {
          bestSubmissions[studentId] = sub;
        }
      }
    });
    
    // Sort by score (highest first), then by time (fastest first)
    const sorted = Object.values(bestSubmissions)
      .sort((a: any, b: any) => {
        const scoreA = a.total_score || 0;
        const scoreB = b.total_score || 0;
        
        if (scoreB !== scoreA) {
          return scoreB - scoreA;
        }
        
        const timeA = new Date(a.finished_at || 0).getTime() - new Date(a.started_at || 0).getTime();
        const timeB = new Date(b.finished_at || 0).getTime() - new Date(b.started_at || 0).getTime();
        
        return timeA - timeB;
      })
      .map((sub: any, idx: number) => ({
        rank: idx + 1,
        ...sub,
        studentName: getSubmissionNameAndClass(sub).name,
        studentClassName: getSubmissionNameAndClass(sub).className,
        studentAvatar: students.find((s: any) => s.id === sub.student_id)?.avatar,
      }));
    
    setRankingData(sorted);
    setSubmissionTab('ranking');
  };

  const handleOpenAllSubmissions = async () => {
    setAllSubsModalOpen(true);
    setAllSubsData([]);
    setAllSubsLoading(true);
    setAllSubsSearch('');
    setAllSubsClassFilter('');
    try {
      const res = await examService.getAllSubmissions();
      setAllSubsData(res.data || []);
    } catch (err: any) {
      dispatch(notificationActions.addNotification('Không tải được danh sách bài nộp', 'DANGER'));
    } finally {
      setAllSubsLoading(false);
    }
  };

  // Click a row → fetch full submissions of that exam, open existing detail modal with pre-selected submission
  const handleAllSubRowClick = async (sub: any) => {
    const examId = sub.exam_id;

    // Guard: if exam_id is null/undefined, can't fetch detail
    if (!examId) {
      dispatch(notificationActions.addNotification('Bài nộp này không xác định được đề thi (đề có thể đã bị xóa)', 'DANGER'));
      return;
    }

    const examInfo = sub.exam || { id: examId, title: `Đề #${examId}`, total_marks: 100 };

    // Keep allSubsModal open — just fetch and set the right panel state
    setSubmissionsExam(examInfo);
    setSelectedSubmission(null);
    setSelectedSubmissionForDetail(null);
    setSubmissionsLoading(true);
    setSubmissionsError(null);

    try {
      const [submissionsRes] = await Promise.all([
        examService.getSubmissions(examId),
        dispatch(userActions.getAllStudents() as any),
        dispatch(classActions.getAll() as any),
        dispatch(studentActions.getAll() as any),
      ]);

      const list: any[] = submissionsRes.data || [];
      setSubmissions(list);

      // Group submissions by student
      const grouped: Record<number, any[]> = {};
      list.forEach((s: any) => {
        const sid = s.student_id;
        if (!grouped[sid]) grouped[sid] = [];
        grouped[sid].push(s);
      });

      // Find the student group that contains clicked sub.id
      const matchGroup = Object.values(grouped).find((g: any[]) =>
        g.some((s: any) => s.id === sub.id)
      );

      if (matchGroup) {
        const sorted = [...matchGroup].sort((a: any, b: any) =>
          new Date(b.finished_at || 0).getTime() - new Date(a.finished_at || 0).getTime()
        );
        const exact = sorted.find((s: any) => s.id === sub.id);
        const chosen = exact
          ? { ...exact, _allSubmissions: sorted, _submissionCount: sorted.length }
          : { ...sorted[0], _allSubmissions: sorted, _submissionCount: sorted.length };
        setSelectedSubmission(chosen);
        setSelectedSubmissionForDetail(chosen);
      }
    } catch (err: any) {
      const msg = err?.toString() || 'Không tải được bài làm';
      setSubmissionsError(msg);
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const handleSelectAllStudents = () => {
    const allStudentIds = filteredStudentsForAssign.map((u: any) => u.id);
    setSelectedStudentsForAssign(prev => {
      const combined = Array.from(new Set([...prev, ...allStudentIds]));
      return combined;
    });
  };

  const handleDeselectAllStudents = () => {
    const allStudentIds = filteredStudentsForAssign.map((u: any) => u.id);
    setSelectedStudentsForAssign(prev => prev.filter(id => !allStudentIds.includes(id)));
  };

  const handleSelectClassStudents = () => {
    if (!selectedClassForAssign) return;
    const classStudentIds = filteredStudentsForAssign
      .filter((u: any) => getStudentInfo(u.id)?.student?.classId === selectedClassForAssign)
      .map((u: any) => u.id);
    setSelectedStudentsForAssign(prev => {
      const combined = Array.from(new Set([...prev, ...classStudentIds]));
      return combined;
    });
  };

  const handleCardClick = (exam: any) => {
    setSelectedExam(exam);
    setShowPreviewModal(true);
  };

  const handleFormClose = () => {
    setShowFormModal(false);
    setSelectedExam(null);
    fetchExams();
  };

  return (
    <Container>
      <Header>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <CreateButton onClick={handleCreateNew}>
            + Tạo Đề Mới
          </CreateButton>
          <button
            onClick={handleOpenAllSubmissions}
            style={{
              padding: '10px 18px',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
            }}
          >
            📋 Bài nộp gần đây
          </button>
        </div>
        <FilterBar>
          <SearchInput
            type="text"
            placeholder="🔍 Tìm kiếm đề thi..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <StatusTagGroup>
            <StatusTag active={statusFilter === ''} statusType="all" onClick={() => setStatusFilter('')}>
              Tất cả
            </StatusTag>
            <StatusTag active={statusFilter === 'draft'} statusType="draft" onClick={() => setStatusFilter('draft')}>
              Nháp
            </StatusTag>
            <StatusTag active={statusFilter === 'published'} statusType="published" onClick={() => setStatusFilter('published')}>
              Đã xuất bản
            </StatusTag>
          </StatusTagGroup>

          <FilterSelect value={difficultyFilter} onChange={(e) => setDifficultyFilter(e.target.value)}>
            <option value="">Tất cả độ khó</option>
            <option value="easy">Dễ</option>
            <option value="medium">Trung bình</option>
            <option value="hard">Khó</option>
          </FilterSelect>

          <FilterSelect 
            value={creatorFilter} 
            onChange={(e) => setCreatorFilter(e.target.value)}
            disabled={!usersLoaded}
          >
            <option value="">{!usersLoaded ? 'Đang tải...' : 'Tất cả người tạo'}</option>
            {usersLoaded && users && users.filter((u: any) => u?.role === 0 || u?.role === 1).map((u: any) => (
              <option key={u.id} value={u.id}>{u.userName || u.name || `User ${u.id}`}</option>
            ))}
          </FilterSelect>
        </FilterBar>
      </Header>

      {loading && examsList.length === 0 ? (
        <CardsGrid>
          {[1, 2, 3, 4, 5, 6].map(i => <LoadingCard key={i} />)}
        </CardsGrid>
      ) : examsList.length === 0 ? (
        <EmptyState>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10,19L12,15H9V10L7,14H10V19Z" />
          </svg>
          <h3>Chưa có đề thi nào</h3>
          <p>Bắt đầu tạo đề thi đầu tiên của bạn!</p>
          <CreateButton onClick={handleCreateNew}>
            + Tạo Đề Thi Đầu Tiên
          </CreateButton>
        </EmptyState>
      ) : (
        <>
          <CardsGrid>
            {examsList.map((exam: Exam, index: number) => {
              const submissionCount = submissionCounts[exam.id]
                ?? (exam as any).submission_count
                ?? (exam as any).submissions_count
                ?? ((exam as any).submissions ? (exam as any).submissions.length : undefined);
              const creatorId = Number((exam as any).created_by ?? (exam as any).creator_id);
              const creatorName = (!Number.isNaN(creatorId) && userNameMap[creatorId]) 
                || (exam as any).creator_name
                || (exam as any).created_by_name
                || '';

              return (
                <ExamCard 
                  key={exam.id} 
                  status={exam.status}
                  onClick={() => handleCardClick(exam)}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <CardContent>
                    <RowCommon>
                      <InfoColumn>
                        <CardHeader>
                          <CardHeaderContent>
                            <TitleRow>
                              <StatusBadge status={exam.status}>
                                {exam.status === 'published' ? 'Đã xuất bản' : 'Nháp'}
                              </StatusBadge>
                              <ExamTitle>{exam.title}</ExamTitle>
                            </TitleRow>
                            {exam.applicable_classes && (
                              <ClassInfo>
                                📚 {exam.applicable_classes}
                              </ClassInfo>
                            )}
                            {creatorName && (
                              <ClassInfo>
                                👤 {creatorName}
                              </ClassInfo>
                            )}
                          </CardHeaderContent>
                        </CardHeader>

                        {exam.description && (
                          <ExamDescription>{exam.description}</ExamDescription>
                        )}
                      </InfoColumn>

                      <MetaColumn>
                        <MetaRow>
                          <MetaItem>
                            <span>📋</span>
                            <MetaLabel>{exam.total_questions || 0}</MetaLabel> câu
                          </MetaItem>
                          <MetaItem>
                            <span>⏱️</span>
                            <MetaLabel>{exam.duration}</MetaLabel> phút
                          </MetaItem>
                          {exam.total_marks && (
                            <MetaItem>
                              <span>⭐</span>
                              <MetaLabel>{exam.total_marks}</MetaLabel> điểm
                            </MetaItem>
                          )}
                          {(exam.difficulty_level || exam.avg_difficulty) && (
                            <DifficultyBadge level={(exam.difficulty_level || exam.avg_difficulty) as string}>
                              {exam.difficulty_level || exam.avg_difficulty}
                            </DifficultyBadge>
                          )}
                          {exam.status === 'published' && (
                            <MetaItem>
                              <span>📥</span>
                              <MetaLabel>{submissionCount ?? 0}</MetaLabel> bài nộp
                            </MetaItem>
                          )}
                        </MetaRow>

                        <CardActions>
                          {exam.status === 'published' && exam.share_link && (
                            <CopyButton onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(`${window.location.origin}/exam/${exam.share_link}`);
                            }}>
                              📋 Copy Link
                            </CopyButton>
                          )}
                          
                          <ActionButton 
                            variant="primary" 
                            onClick={(e) => handleEdit(exam, e)}
                            title={exam.status === 'published' ? 'Đề thi đã xuất bản không thể sửa. Click để tạo bản sao.' : 'Sửa đề thi'}
                            style={exam.status === 'published' ? { 
                              opacity: 0.6,
                              cursor: 'not-allowed'
                            } : {}}
                          >
                            {exam.status === 'published' ? '🔒 Sửa' : '✏️ Sửa'}
                          </ActionButton>
                          <ActionButton variant="secondary" onClick={(e) => handleDuplicate(exam.id, e)}>
                            📋 Sao chép đề
                          </ActionButton>
                          
                          {exam.status === 'published' && (
                            <>
                              <ActionButton variant="secondary" onClick={(e) => handleOpenAssignModal(exam, e)}>
                                👥 Gán học sinh
                              </ActionButton>
                              <ActionButton variant="secondary" onClick={(e) => handleViewSubmissions(exam, e)}>
                                📊 Bài làm
                              </ActionButton>
                            </>
                          )}
                          
                          <ActionButton variant="danger" onClick={(e) => handleDelete(exam.id, e)}>
                            🗑️ Xóa
                          </ActionButton>
                        </CardActions>
                      </MetaColumn>
                    </RowCommon>
                  </CardContent>
                </ExamCard>
              );
            })}
          </CardsGrid>

          {pagination.totalPages > 0 && (
            <Pagination>
              <PageButton
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                ← Trước
              </PageButton>
              <PageButton active={true} style={{ cursor: 'default' }}>
                {currentPage}/{pagination.totalPages}
              </PageButton>
              <PageButton
                onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}
                disabled={currentPage === pagination.totalPages}
              >
                Sau →
              </PageButton>
            </Pagination>
          )}
        </>
      )}

      {showFormModal && (
        <ExamFormModal
          exam={selectedExam}
          onClose={handleFormClose}
        />
      )}

      {showPreviewModal && selectedExam && (
        <ExamPreviewModal
          examId={selectedExam.id}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedExam(null);
          }}
        />
      )}

      {submissionsModalOpen && (
        <Overlay onClick={() => setSubmissionsModalOpen(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeaderBar>
              <div>
                <strong>Bài làm</strong> {submissionsExam?.title ? `- ${submissionsExam.title}` : ''}
              </div>
              <CloseBtn onClick={() => setSubmissionsModalOpen(false)}>Đóng</CloseBtn>
            </ModalHeaderBar>
            {submissionsLoading ? (
              <div>Đang tải...</div>
            ) : submissionsError ? (
              <div style={{ color: 'red' }}>{submissionsError}</div>
            ) : submissions.length === 0 ? (
              <div>Chưa có bài nộp.</div>
            ) : (
              <ModalContentGrid>
                <Panel>
                  <PanelHeader>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 0, justifyContent: 'space-between', width: '100%' }}>
                      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0' }}>
                        <button
                          onClick={() => setSubmissionTab('list')}
                          style={{
                            padding: '10px 16px',
                            fontSize: '14px',
                            fontWeight: submissionTab === 'list' ? '700' : '500',
                            background: 'none',
                            border: 'none',
                            borderBottom: submissionTab === 'list' ? '2px solid #2563eb' : 'none',
                            color: submissionTab === 'list' ? '#2563eb' : '#666',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            marginBottom: '-2px',
                          }}
                        >
                          📋 Chi tiết ({new Set(submissions.map(s => s.student_id)).size} bạn nộp/{submissions.length} bài nộp)
                        </button>
                        <button
                          onClick={handleOpenRanking}
                          style={{
                            padding: '10px 16px',
                            fontSize: '14px',
                            fontWeight: submissionTab === 'ranking' ? '700' : '500',
                            background: 'none',
                            border: 'none',
                            borderBottom: submissionTab === 'ranking' ? '2px solid #2563eb' : 'none',
                            color: submissionTab === 'ranking' ? '#2563eb' : '#666',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            marginBottom: '-2px',
                          }}
                        >
                          🏆 Xếp hạng
                        </button>
                      </div>
                    </div>
                  </PanelHeader>
                  <PanelBody>
                    {submissionTab === 'list' ? (
                      <>
                        <div style={{ marginBottom: 10 }}>
                          <input
                            type="text"
                            value={submissionSearch}
                            onChange={(e) => setSubmissionSearch(e.target.value)}
                        placeholder="Tìm theo tên hoặc lớp học sinh..."
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: 8,
                          border: '1px solid #e2e8f0',
                          fontSize: 14,
                        }}
                      />
                    </div>
                    <SubmissionsTable>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Tên</th>
                          <th>Điểm</th>
                          <th>Thời gian làm</th>
                          <th>Ngày nộp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSubmissions.map((s, idx) => (
                          <tr
                            key={s.id || idx}
                            style={{ cursor: 'pointer', background: selectedSubmissionForDetail?.student_id === s.student_id ? '#eef2ff' : undefined }}
                            onClick={() => {
                              setSelectedSubmissionForDetail(s);
                              setSelectedSubmission(s);
                            }}
                          >
                            <td>{idx + 1}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span>{getSubmissionNameAndClass(s).name} ({getSubmissionNameAndClass(s).className})</span>
                                {s._submissionCount && s._submissionCount > 1 && (
                                  <span style={{ color: '#dc2626', fontWeight: '700', fontSize: '13px' }}>
                                    {s._submissionCount} lần
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              {s.total_score}
                              {submissionsExam?.total_marks ? `/${submissionsExam.total_marks}` : ''}
                            </td>
                            <td>
                              {(() => {
                                if (!s.started_at || !s.finished_at) return '—';
                                const start = new Date(s.started_at).getTime();
                                const end = new Date(s.finished_at).getTime();
                                const diffMs = end - start;
                                const minutes = Math.floor(diffMs / 60000);
                                const seconds = Math.floor((diffMs % 60000) / 1000);
                                return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                              })()}
                            </td>
                            <td>{formatDateTime(s.finished_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </SubmissionsTable>
                      </>
                    ) : (
                      /* Ranking Tab */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {rankingData.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999', fontSize: '14px' }}>
                            Chưa có bài nộp để xếp hạng
                          </div>
                        ) : (
                          rankingData.map((item) => (
                            <div
                              key={item.id}
                              style={{
                                background: item.rank === 1 ? 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)' : 
                                           item.rank === 2 ? 'linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%)' :
                                           item.rank === 3 ? 'linear-gradient(135deg, #cd7f32 0%, #e5b87a 100%)' :
                                           'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                                borderRadius: '12px',
                                padding: '12px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer',
                              }}
                              onMouseEnter={(e) => {
                                (e.currentTarget as HTMLDivElement).style.transform = 'translateX(4px)';
                                (e.currentTarget as HTMLDivElement).style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.12)';
                              }}
                              onMouseLeave={(e) => {
                                (e.currentTarget as HTMLDivElement).style.transform = 'translateX(0)';
                                (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                              }}
                            >
                              <div
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  background: item.rank === 1 ? '#ffc107' : item.rank === 2 ? '#c0c0c0' : item.rank === 3 ? '#cd7f32' : '#e0e0e0',
                                  color: item.rank <= 3 ? 'white' : '#333',
                                  fontWeight: '700',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '18px',
                                  flexShrink: 0,
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                }}
                              >
                                {item.rank}
                              </div>
                              
                              {item.studentAvatar ? (
                                <img
                                  src={getImageUrl(item.studentAvatar)}
                                  alt={item.studentName}
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    flexShrink: 0,
                                    border: '2px solid white',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                  }}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: '700',
                                    flexShrink: 0,
                                    border: '2px solid white',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                  }}
                                >
                                  {(item.studentName || 'H')[0].toUpperCase()}
                                </div>
                              )}
                              
                              <div style={{ flex: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                                <div style={{ fontWeight: '600', color: '#333', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  {item.studentName || 'Ẩn danh'} ({item.studentClassName || 'Tự Do'})
                                  {item.rank === 1 && <span style={{ fontSize: '18px' }}>🥇</span>}
                                  {item.rank === 2 && <span style={{ fontSize: '18px' }}>🥈</span>}
                                  {item.rank === 3 && <span style={{ fontSize: '18px' }}>🥉</span>}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'right' }}>
                                  <div style={{ fontSize: '13px', color: '#000', fontWeight: '700' }}>
                                    {item.total_score}
                                    {item.total_marks ? `/${item.total_marks}` : ''} điểm
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#000', fontWeight: '700' }}>
                                    Thời gian làm: {(() => {
                                      if (!item.started_at || !item.finished_at) return '—';
                                      const start = new Date(item.started_at).getTime();
                                      const end = new Date(item.finished_at).getTime();
                                      const diffMs = end - start;
                                      const minutes = Math.floor(diffMs / 60000);
                                      const seconds = Math.floor((diffMs % 60000) / 1000);
                                      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </PanelBody>
                </Panel>

                <Panel>
                  <PanelHeader>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <div>
                        {selectedSubmission ? `Chi tiết bài nộp của ${getSubmissionNameAndClass(selectedSubmission).name} (${getSubmissionNameAndClass(selectedSubmission).className})` : 'Chọn một bài nộp để xem chi tiết'}
                      </div>
                      {selectedSubmission && selectedSubmission._submissionCount && selectedSubmission._submissionCount > 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <label style={{ fontSize: '13px', color: '#475569', fontWeight: '500', whiteSpace: 'nowrap' }}>
                            Chọn lần nộp:
                          </label>
                          <SubmissionSelector
                            value={selectedSubmission?.id || ''}
                            onChange={(e) => {
                              const submissionId = parseInt(e.target.value);
                              const allSubs = selectedSubmissionForDetail?._allSubmissions || selectedSubmission._allSubmissions || [];
                              const submissionCount = selectedSubmissionForDetail?._submissionCount || selectedSubmission._submissionCount || 1;
                              const chosen = allSubs.find((sub: any) => sub.id === submissionId);
                              if (chosen) {
                                // Preserve metadata when switching submissions
                                chosen._submissionCount = submissionCount;
                                chosen._allSubmissions = allSubs;
                                setSelectedSubmission(chosen);
                                setSelectedSubmissionForDetail(chosen);
                                // Scroll to top of detail panel
                                setTimeout(() => {
                                  if (detailPanelBodyRef.current) {
                                    detailPanelBodyRef.current.scrollTop = 0;
                                  }
                                }, 0);
                              }
                            }}
                          >
                            {(selectedSubmissionForDetail?._allSubmissions || selectedSubmission._allSubmissions || []).map((sub: any, idx: number) => {
                              const totalCount = selectedSubmissionForDetail?._submissionCount || selectedSubmission._submissionCount || 1;
                              return (
                                <option key={sub.id} value={sub.id}>
                                  Lần {totalCount - idx} (Điểm: {sub.total_score}
                                  {submissionsExam?.total_marks ? `/${submissionsExam.total_marks}` : ''})
                                </option>
                              );
                            })}
                          </SubmissionSelector>
                        </div>
                      )}
                    </div>
                  </PanelHeader>
                  <PanelBody ref={detailPanelBodyRef}>
                    {!selectedSubmission ? (
                      <div>Chọn một bài nộp để xem chi tiết.</div>
                    ) : (() => {
                      const userAnswers = selectedSubmission.user_answers || [];
                      
                      // Count correct/wrong considering sub-questions
                      let totalCorrectSubQuestions = 0;
                      let totalWrongSubQuestions = 0;
                      
                      userAnswers.forEach((ua: any) => {
                        if (ua.total_sub_questions && ua.total_sub_questions > 1) {
                          // Special type with sub-questions
                          totalCorrectSubQuestions += ua.correct_count || 0;
                          totalWrongSubQuestions += (ua.total_sub_questions - (ua.correct_count || 0));
                        } else {
                          // Single question type
                          if (ua.is_correct === true || ua.is_correct === 1) {
                            totalCorrectSubQuestions += 1;
                          } else {
                            totalWrongSubQuestions += 1;
                          }
                        }
                      });
                      
                      // Filter: show question if at least one sub-question matches filter
                      const visibleAnswers = (() => {
                        if (answerFilter === 'correct') {
                          return userAnswers.filter((ua: any) => {
                            if (ua.total_sub_questions && ua.total_sub_questions > 1) {
                              return (ua.correct_count || 0) > 0; // Has at least 1 correct sub-question
                            }
                            return ua.is_correct === true || ua.is_correct === 1;
                          });
                        }
                        if (answerFilter === 'wrong') {
                          return userAnswers.filter((ua: any) => {
                            if (ua.total_sub_questions && ua.total_sub_questions > 1) {
                              const wrongCount = ua.total_sub_questions - (ua.correct_count || 0);
                              return wrongCount > 0; // Has at least 1 wrong sub-question
                            }
                            return ua.is_correct === false || ua.is_correct === 0;
                          });
                        }
                        return userAnswers;
                      })();
                      
                      // Group by question type
                      const grouped: Record<number, any[]> = {};
                      visibleAnswers.forEach((ua: any) => {
                        const typeId = ua.question?.question_type_id || 0;
                        if (!grouped[typeId]) {
                          grouped[typeId] = [];
                        }
                        grouped[typeId].push(ua);
                      });
                      
                      // Create groups in order
                      const groups = Object.entries(grouped)
                        .sort(([a], [b]) => parseInt(a) - parseInt(b))
                        .map(([_, answers]) => {
                          // Sort within group by order_index
                          return answers.slice().sort((a: any, b: any) => {
                            const ao = a.question?.order_index ?? 0;
                            const bo = b.question?.order_index ?? 0;
                            return ao - bo;
                          });
                        });
                      
                      // Create global question number map
                      const qNumberMap = new Map<number, number>();
                      let counter = 1;
                      groups.forEach((grp) => {
                        grp.forEach((ua: any) => {
                          if (ua.question?.id) {
                            qNumberMap.set(ua.question.id, counter);
                            counter += 1;
                          }
                        });
                      });
                      
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                          <div style={{ display: 'flex', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                            <TagButton active={answerFilter === 'all'} onClick={() => setAnswerFilter('all')}>
                              Tất cả ({totalCorrectSubQuestions + totalWrongSubQuestions} câu nhỏ)
                            </TagButton>
                            <TagButton active={answerFilter === 'correct'} onClick={() => setAnswerFilter('correct')}>
                              Đúng ({totalCorrectSubQuestions})
                            </TagButton>
                            <TagButton active={answerFilter === 'wrong'} onClick={() => setAnswerFilter('wrong')}>
                              Sai ({totalWrongSubQuestions})
                            </TagButton>
                          </div>
                          
                          {/* Submission timestamps and duration */}
                          <div style={{ 
                            display: 'flex',
                            gap: 16,
                            padding: '10px',
                            background: '#f0f9ff',
                            borderRadius: '8px',
                            fontSize: '13px',
                            borderLeft: '3px solid #3b82f6',
                            flexWrap: 'wrap',
                            alignItems: 'center'
                          }}>
                            <div>
                              <span style={{ color: '#0f172a', fontWeight: '700', fontSize: '14px' }}>Điểm: </span>
                              <span style={{ fontWeight: '600' }}>{selectedSubmission?.total_score}
                              {submissionsExam?.total_marks ? `/${submissionsExam.total_marks}` : ''}</span>
                            </div>
                            <div>
                              <span style={{ color: '#0f172a', fontWeight: '700', fontSize: '14px' }}>Bắt đầu: </span>
                              <span style={{ fontWeight: '600' }}>{selectedSubmission?.started_at ? formatDateTime(selectedSubmission.started_at) : '—'}</span>
                            </div>
                            <div>
                              <span style={{ color: '#0f172a', fontWeight: '700', fontSize: '14px' }}>Nộp: </span>
                              <span style={{ fontWeight: '600' }}>{selectedSubmission?.finished_at ? formatDateTime(selectedSubmission.finished_at) : '—'}</span>
                            </div>
                            <div>
                              <span style={{ color: '#0f172a', fontWeight: '700', fontSize: '14px' }}>Thời gian làm: </span>
                              <span style={{ fontWeight: '600' }}>
                                {(() => {
                                  if (!selectedSubmission?.started_at || !selectedSubmission?.finished_at) return '—';
                                  const start = new Date(selectedSubmission.started_at).getTime();
                                  const end = new Date(selectedSubmission.finished_at).getTime();
                                  const diffMs = end - start;
                                  const hours = Math.floor(diffMs / 3600000);
                                  const minutes = Math.floor((diffMs % 3600000) / 60000);
                                  const seconds = Math.floor((diffMs % 60000) / 1000);
                                  if (hours > 0) {
                                    return `${hours}h ${minutes}m ${seconds}s`;
                                  }
                                  return `${minutes}m ${seconds}s`;
                                })()}
                              </span>
                            </div>
                          </div>
                          {groups.map((group, exerciseIdx) => {
                            const firstQ = group[0]?.question;
                            const typeInfo = firstQ?.questionType || {};
                            const engTitle = typeInfo.eng_title || '';
                            const vnTitle = typeInfo.vn_title || '';
                            const headerText = `Exercise ${exerciseIdx + 1}: ${engTitle}${vnTitle ? ` (${vnTitle})` : ''}`;
                            
                            return (
                              <div key={`exercise-${exerciseIdx}`}>
                                <h4 style={{
                                  color: '#1d4ed8',
                                  fontSize: 16,
                                  fontWeight: 700,
                                  margin: '0 0 12px 0',
                                  borderBottom: '2px solid #bfdbfe',
                                  paddingBottom: 6
                                }}>
                                  {headerText}
                                </h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                  {group.map((ua: any, idx: number) => {
                                    const q = ua.question || {};
                                    let content: any = q.content || {};
                                    if (typeof content === 'string') {
                                      try {
                                        content = JSON.parse(content);
                                      } catch {
                                        content = {};
                                      }
                                    }
                                    const questionTypeId = q.question_type_id || q.questionType?.id;
                                    const correct = (() => {
                                      switch (questionTypeId) {
                                        case 1: {
                                          if (content.options && content.correct_index !== undefined) {
                                            const label = String.fromCharCode(65 + content.correct_index);
                                            const text = content.options[content.correct_index];
                                            return text ? `${label}. ${text}` : label;
                                          }
                                          break;
                                        }
                                        case 2:
                                          return content.correct_answer;
                                        case 3: {
                                          // Type 3: Sentence Ordering - correct_answers array or correct_sentence (backward compatibility)
                                          if (content?.correct_answers && Array.isArray(content.correct_answers) && content.correct_answers.length > 0) {
                                            return content.correct_answers.join(' | ');
                                          }
                                          if (content?.correct_sentence) {
                                            return content.correct_sentence;
                                          }
                                          return null;
                                        }
                                        case 4: {
                                          // Type 4: Write Sentence - prompts array with multiple answers
                                          if (Array.isArray(content.prompts)) {
                                            const allAnswers: string[] = [];
                                            for (const p of content.prompts) {
                                              if (p.answers && Array.isArray(p.answers)) {
                                                // New format: multiple answers per prompt
                                                allAnswers.push(p.answers.filter(Boolean).join(' / '));
                                              } else if (p.answer) {
                                                // Old format: single answer
                                                allAnswers.push(p.answer);
                                              }
                                            }
                                            if (allAnswers.length) return allAnswers.join(' | ');
                                          }
                                          break;
                                        }
                                        case 5: {
                                          // Type 5: Rewrite Sentence - correct_answers array or correct_answer (backward compatibility)
                                          if (content?.correct_answers && Array.isArray(content.correct_answers) && content.correct_answers.length > 0) {
                                            return content.correct_answers.join(' | ');
                                          }
                                          if (content?.correct_answer) {
                                            return content.correct_answer;
                                          }
                                          return null;
                                        }
                                        case 9: {
                                          // Type 9: Fill Blanks MCQ - use full option text with letter prefix
                                          if (Array.isArray(content.questions)) {
                                            return content.questions
                                              .map((q: any, i: number) => {
                                                if (q.options && q.correct_index !== undefined) {
                                                  const label = String.fromCharCode(65 + q.correct_index);
                                                  const text = q.options[q.correct_index];
                                                  return `${i + 1}) ${label}. ${text}`;
                                                }
                                                return `${i + 1}) —`;
                                              })
                                              .join(' | ');
                                          }
                                          break;
                                        }
                                                                                case 8: {
                                                                                  // Type 8: Fill Blanks Word Bank - answers array
                                                                                  if (Array.isArray(content.answers)) {
                                                                                    return content.answers.join(', ');
                                                                                  }
                                                                                  break;
                                                                                }
                                        default:
                                          break;
                                      }
                                      if (q.correctAnswers && q.correctAnswers[0]) return q.correctAnswers[0].answer;
                                      return null;
                                    })();
                                    const correctCandidates =
                                      shouldHighlightWordDiff(questionTypeId) && typeof correct === 'string'
                                        ? splitCorrectAnswerCandidates(correct)
                                        : [];
                                    const acceptedLabel =
                                      correctCandidates.length > 1 ? 'Đáp án đúng được chấp nhận:' : 'Đáp án đúng:';
                                    const renderAcceptedAnswers = () => {
                                      if (correctCandidates.length > 1) {
                                        return (
                                          <span style={{ display: 'inline-block' }}>
                                            {correctCandidates.map((ans, idx) => (
                                              <div key={idx}>{ans}</div>
                                            ))}
                                          </span>
                                        );
                                      }
                                      return correct;
                                    };
                                    const isCorrect = ua.is_correct;
                                    const renderText = () => {
                                      const c = content || {};
                                      // Type 3: Sentence Ordering - show words
                                      if (questionTypeId === 3 && Array.isArray(c.words)) return c.words.join(' / ');
                                      // Type 4: Write Sentence - show first prompt
                                      if (questionTypeId === 4 && c.prompts && c.prompts.length > 0) return c.prompts[0].text;
                                      // Type 6, 7, 9: Reading - show passage
                                      if ((questionTypeId === 6 || questionTypeId === 7 || questionTypeId === 9) && c.passage) {
                                        const blankRegex = /_{3,}|\.{3,}/g;
                                        let blankIndex = 1;
                                        const textWithIndexes = c.passage.replace(blankRegex, () => `(${blankIndex++}) ____`);
                                        return textWithIndexes;
                                      }
                                                                            // Type 8: Fill Blanks Word Bank - show text
                                                                            if (questionTypeId === 8 && c.text) return c.text;
                                      // Type 11: Make Question - show sentence with underlined
                                      if (questionTypeId === 11 && c.sentence) {
                                        const sentence = c.sentence;
                                        const underlined = c.underlined || '';
                                        if (underlined && sentence.includes(underlined)) {
                                          const parts = sentence.split(underlined);
                                          return (
                                            <>
                                              {parts[0]}<u style={{fontWeight:'bold', textDecoration:'underline'}}>{underlined}</u>{parts[1]||''}
                                              <div style={{marginTop: 4}}>____________________________________________________</div>
                                            </>
                                          );
                                        }
                                        return sentence;
                                      }
                                      // Default
                                      return c.text || c.original_sentence || c.sentence || '';
                                    };
                                    const qNumber = qNumberMap.get(q.id) || idx + 1;
                                    const explanation = q?.explanation;
                                    const explanationOpen = q?.id ? showExplanationMap[q.id] : false;
                                    const toggleExplanation = () => {
                                      if (!q?.id) return;
                                      setShowExplanationMap(prev => ({ ...prev, [q.id]: !prev[q.id] }));
                                    };
                                    
                                    return (
                                      <AnswerCard key={ua.id || idx}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <div style={{ fontWeight: 700 }}>
                                            {qNumber}. [
                                            <a
                                              href={`/questions-manage?search=${q.id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                color: '#2563eb',
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                                fontWeight: 700,
                                              }}
                                              onMouseEnter={(e) => {
                                                (e.target as HTMLAnchorElement).style.color = '#1d4ed8';
                                              }}
                                              onMouseLeave={(e) => {
                                                (e.target as HTMLAnchorElement).style.color = '#2563eb';
                                              }}
                                            >
                                              {q.id}
                                            </a>
                                            ] {renderText()}
                                          </div>
                                          {(() => {
                                            // Show sub-question stats for special types
                                            if (ua.total_sub_questions && ua.total_sub_questions > 1) {
                                              const correctCount = ua.correct_count || 0;
                                              const wrongCount = ua.total_sub_questions - correctCount;
                                              return (
                                                <div style={{ display: 'flex', gap: 4 }}>
                                                  {correctCount > 0 && <Pill variant="success">Đúng({correctCount})</Pill>}
                                                  {wrongCount > 0 && <Pill variant="danger">Sai({wrongCount})</Pill>}
                                                </div>
                                              );
                                            }
                                            // Single question - show simple tag
                                            return <Pill variant={isCorrect ? 'success' : 'danger'}>{isCorrect ? 'Đúng' : 'Sai'}</Pill>;
                                          })()}
                                        </div>
                                        <div style={{ marginTop: 6 }}>
                                          {/* Display passage and statements for type 12 */}
                                          {questionTypeId === 12 && content?.passage && (
                                            <div style={{ fontSize: 13, color: '#0f172a', marginBottom: 12, fontStyle: 'italic' }}>
                                              {content.passage}
                                            </div>
                                          )}
                                          {questionTypeId === 12 && Array.isArray(content?.statements) && (
                                            <div style={{ fontSize: 13, color: '#0f172a', marginBottom: 12 }}>
                                              {content.statements.map((stmt: any, i: number) => (
                                                <div key={i}>{i + 1}) {stmt.text}</div>
                                              ))}
                                            </div>
                                          )}
                                          <div style={{ fontSize: 13, color: '#0f172a' }}>
                                            <strong>Câu trả lời:</strong> {(() => {
                                              // Format type 12 answers with color coding
                                              if (questionTypeId === 12 && Array.isArray(content?.statements)) {
                                                const answerParts = ua.answer ? ua.answer.split(' | ').map((s: string) => s.trim()) : [];
                                                return (
                                                  <span>
                                                    {content.statements.map((_: any, i: number) => {
                                                      const ans = answerParts[i] || '—';
                                                      const correctVal = content.statements[i]?.is_true ? 'T' : 'F';
                                                      const normAns = ans.toUpperCase() === 'TRUE' ? 'T' : ans.toUpperCase() === 'FALSE' ? 'F' : ans.toUpperCase();
                                                      const isCorrect = normAns === correctVal;
                                                      const displayAns = normAns === 'T' ? 'True' : normAns === 'F' ? 'False' : normAns;
                                                      return (
                                                        <span key={i}>
                                                          {i > 0 && '  -  '}
                                                          <span style={{ color: isCorrect ? '#65a30d' : '#dc2626', fontWeight: 600 }}>
                                                            {i + 1}.{displayAns}
                                                          </span>
                                                        </span>
                                                      );
                                                    })}
                                                  </span>
                                                );
                                              }
                                              // Format type 9 answers with option letters
                                              if (questionTypeId === 9 && content?.questions && Array.isArray(content.questions)) {
                                                const answerParts = ua.answer ? ua.answer.split(' | ').map((s: string) => s.trim()) : [];
                                                return answerParts.map((ans: string, i: number) => {
                                                  const subQ = content.questions[i];
                                                  if (subQ?.options) {
                                                    const optIdx = subQ.options.findIndex((opt: string) => opt === ans);
                                                    if (optIdx !== -1) {
                                                      const label = String.fromCharCode(65 + optIdx);
                                                      return `${i + 1}) ${label}. ${ans}`;
                                                    }
                                                  }
                                                  return `${i + 1}) ${ans}`;
                                                }).join(' | ');
                                              }
                                              // Format type 10 answer with option letter
                                              if (questionTypeId === 10 && Array.isArray(content?.options)) {
                                                const userIdx = Number(ua.answer);
                                                if (!Number.isNaN(userIdx) && content.options[userIdx] !== undefined) {
                                                  return `${String.fromCharCode(65 + userIdx)}. ${content.options[userIdx]}`;
                                                }
                                              }
                                              if (!isCorrect && shouldHighlightWordDiff(questionTypeId) && typeof ua.answer === 'string' && correctCandidates.length > 0) {
                                                return highlightMismatchedWords(ua.answer, correctCandidates);
                                              }
                                              return formatAnswer(ua.answer);
                                            })()}
                                          </div>
                                          <div style={{ fontSize: 13, color: '#475569', marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            <strong>{acceptedLabel}</strong>
                                            <span>{(() => {
                                            // Format type 12 correct answers - show True/False with dashes
                                            if (questionTypeId === 12 && Array.isArray(content?.statements)) {
                                              return (
                                                <span>
                                                  {content.statements.map((stmt: any, i: number) => {
                                                    const correctVal = stmt.is_true ? 'True' : 'False';
                                                    return (
                                                      <span key={i}>
                                                        {i > 0 && '  -  '}
                                                        {i + 1}.{correctVal}
                                                      </span>
                                                    );
                                                  })}
                                                </span>
                                              );
                                            }
                                            // Format type 10 correct answer with option letter and correction
                                            if (questionTypeId === 10 && Array.isArray(content?.options)) {
                                              const wrongIdx = content.wrong_index;
                                              const correction = content.correct_answer || content.correction || '';
                                              if (wrongIdx !== undefined && content.options[wrongIdx] !== undefined) {
                                                const label = String.fromCharCode(65 + wrongIdx);
                                                return `${label}. ${content.options[wrongIdx]} → ${correction}`;
                                              }
                                            }
                                            // Format type 11 correct answer - show correct_questions array or correct_question
                                            if (questionTypeId === 11) {
                                              if (content?.correct_questions && Array.isArray(content.correct_questions) && content.correct_questions.length > 0) {
                                                return content.correct_questions.join(' | ');
                                              }
                                              if (content?.correct_question) {
                                                return content.correct_question;
                                              }
                                            }
                                            return renderAcceptedAnswers();
                                            })()}</span>
                                          </div>
                                          {explanation && (
                                            <div style={{ marginTop: 8 }}>
                                              <button
                                                type="button"
                                                onClick={toggleExplanation}
                                                style={{
                                                  background: '#e0f2fe',
                                                  border: '1px solid #93c5fd',
                                                  color: '#0f172a',
                                                  padding: '4px 10px',
                                                  borderRadius: 6,
                                                  cursor: 'pointer',
                                                  fontWeight: 600,
                                                }}
                                              >
                                                {explanationOpen ? 'Ẩn giải thích' : 'Hiện giải thích'}
                                              </button>
                                              {explanationOpen && (
                                                <div style={{ marginTop: 6, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
                                                  {explanation}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                          <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>Điểm: {ua.score}</div>
                                        </div>
                                      </AnswerCard>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </PanelBody>
                </Panel>
              </ModalContentGrid>
            )}
          </ModalBox>
        </Overlay>
      )}

      {showAssignModal && assignToExam && (
        <Overlay onClick={() => setShowAssignModal(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeaderBar>
              <div>
                <strong>Gán đề thi</strong> - {assignToExam?.title}
              </div>
              <CloseBtn onClick={() => setShowAssignModal(false)}>Đóng</CloseBtn>
            </ModalHeaderBar>
            
            <AssignModalContent>
              <div>
                <h4 style={{ margin: '0 0 12px 0', color: '#0f172a', fontWeight: 600 }}>
                  Chọn học sinh để gán đề thi:
                </h4>

                {/* Search Input */}
                <AssignSearchInput
                  type="text"
                  placeholder="🔍 Tìm kiếm theo tên học sinh hoặc userName..."
                  value={assignSearchTerm}
                  onChange={(e) => setAssignSearchTerm(e.target.value)}
                />

                {/* Toolbar: Select All, Class Filter */}
                <AssignToolbar>
                  <AssignIconButton
                    onClick={handleSelectAllStudents}
                    disabled={filteredStudentsForAssign.length === 0}
                  >
                    ✓ Chọn tất cả
                  </AssignIconButton>
                  <AssignIconButton
                    onClick={handleDeselectAllStudents}
                    disabled={filteredStudentsForAssign.length === 0}
                  >
                    ✗ Bỏ chọn tất cả
                  </AssignIconButton>
                  
                  <AssignSelect
                    value={selectedClassForAssign || ''}
                    onChange={(e) => setSelectedClassForAssign(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">-- Chọn lớp --</option>
                    {availableClasses.map((cls: any) => (
                      <option key={cls.id} value={cls.id}>
                        {cls.name}
                      </option>
                    ))}
                  </AssignSelect>

                  {selectedClassForAssign && (
                    <AssignIconButton
                      onClick={handleSelectClassStudents}
                      disabled={filteredStudentsForAssign.filter((u: any) => getStudentInfo(u.id)?.student?.classId === selectedClassForAssign).length === 0}
                    >
                      ✓ Chọn lớp này
                    </AssignIconButton>
                  )}
                </AssignToolbar>

                {/* Student List */}
                <StudentListContainer>
                  {filteredStudentsForAssign.length === 0 ? (
                    <div style={{ padding: '20px 12px', textAlign: 'center', color: '#64748b' }}>
                      {assignSearchTerm || selectedClassForAssign ? 'Không tìm thấy học sinh nào' : 'Không có học sinh'}
                    </div>
                  ) : (
                    filteredStudentsForAssign.map((user: any) => {
                      const isSelected = selectedStudentsForAssign.includes(user.id);
                      const isAlreadyAssigned = assignedStudents.some((assignment: any) => assignment.user?.id === user.id);
                      const info = getStudentInfo(user.id);
                      const studentName = info?.student?.name || 'N/A';
                      const className = info?.studentClass?.name || 'N/A';
                      const studentId = info?.student?.id || 'N/A';

                      return (
                        <StudentCheckbox key={user.id}>
                          <input
                            type="checkbox"
                            id={`student-${user.id}`}
                            checked={isSelected || isAlreadyAssigned}
                            onChange={(e) => handleSelectStudentForAssign(user.id, e.target.checked)}
                            disabled={isAlreadyAssigned && !isSelected}
                          />
                          <label htmlFor={`student-${user.id}`}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                              <span>{user.userName}</span>
                              <span style={{ fontSize: '12px', color: '#64748b' }}>
                                ({studentId} - {studentName} - {className})
                              </span>
                              {isAlreadyAssigned && (
                                <span style={{ marginLeft: 'auto', color: '#10b981', fontSize: '12px', fontWeight: '500' }}>
                                  ✓ Đã gán
                                </span>
                              )}
                            </div>
                          </label>
                        </StudentCheckbox>
                      );
                    })
                  )}
                </StudentListContainer>
              </div>

              {assignedStudents.length > 0 && (
                <AssignedStudentsList>
                  <div className="list-header">
                    📌 Học sinh đã gán ({assignedStudents.length}):
                  </div>
                  <div>
                    {assignedStudents.map((assignment: any) => (
                      <div key={assignment.id} className="student-tag">
                        {assignment.user?.userName || assignment.user_id}
                        <span
                          className="remove-btn"
                          onClick={() => handleRemoveAssignedStudent(assignment.id)}
                          title="Gỡ gán"
                        >
                          ✕
                        </span>
                      </div>
                    ))}
                  </div>
                </AssignedStudentsList>
              )}

              <AssignButtonGroup>
                <AssignButton
                  variant="secondary"
                  onClick={() => setShowAssignModal(false)}
                >
                  Hủy
                </AssignButton>
                <AssignButton
                  variant="primary"
                  disabled={assignLoading || selectedStudentsForAssign.length === 0}
                  onClick={handleSubmitAssign}
                >
                  {assignLoading ? 'Đang gán...' : '✓ Gán đề thi'}
                </AssignButton>
              </AssignButtonGroup>
            </AssignModalContent>
          </ModalBox>
        </Overlay>
      )}


      {/* ─── ALL SUBMISSIONS MODAL (2-column layout) ─── */}
      {allSubsModalOpen && (() => {
        const keyword = allSubsSearch.trim().toLowerCase();
        const filtered = allSubsData.filter((sub: any) => {
          const { name, className } = getSubmissionNameAndClass(sub);
          const examTitle = sub.exam?.title || '';
          const matchSearch = !keyword ||
            name.toLowerCase().includes(keyword) ||
            className.toLowerCase().includes(keyword) ||
            examTitle.toLowerCase().includes(keyword);
          const matchClass = !allSubsClassFilter || className === allSubsClassFilter;
          return matchSearch && matchClass;
        });
        const classNames = Array.from(new Set(
          allSubsData.map((s: any) => getSubmissionNameAndClass(s).className)
        )).filter(Boolean).sort() as string[];

        return (
          <Overlay onClick={() => { setAllSubsModalOpen(false); setSelectedSubmission(null); setSelectedSubmissionForDetail(null); }}>
            <ModalBox
              style={{ maxWidth: 1240, width: '98vw', maxHeight: '93vh', display: 'flex', flexDirection: 'column' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <ModalHeaderBar>
                <div>
                  <strong>📋 Bài nộp gần đây</strong>
                  <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8, fontWeight: 400 }}>
                    {filtered.length}/{allSubsData.length} bài · {selectedSubmission ? `Đang xem: ${getSubmissionNameAndClass(selectedSubmission).name}` : 'Click hàng để xem chi tiết'}
                  </span>
                </div>
                <CloseBtn onClick={() => { setAllSubsModalOpen(false); setSelectedSubmission(null); setSelectedSubmissionForDetail(null); }}>Đóng</CloseBtn>
              </ModalHeaderBar>

              {/* 2-column grid */}
              <ModalContentGrid style={{ flex: 1, minHeight: 0 }}>

                {/* ─── LEFT: submission list ─── */}
                <Panel>
                  <PanelHeader>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <input
                        type="text"
                        placeholder="🔍 Tìm HS, lớp, tên đề..."
                        value={allSubsSearch}
                        onChange={e => setAllSubsSearch(e.target.value)}
                        style={{ flex: 1, minWidth: 120, padding: '5px 9px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12 }}
                      />
                      <select
                        value={allSubsClassFilter}
                        onChange={e => setAllSubsClassFilter(e.target.value)}
                        style={{ padding: '5px 9px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, cursor: 'pointer' }}
                      >
                        <option value="">Tất cả lớp</option>
                        {classNames.map((cn) => (
                          <option key={cn} value={cn}>{cn}</option>
                        ))}
                      </select>
                    </div>
                  </PanelHeader>
                  <PanelBody>
                    {allSubsLoading ? (
                      <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>⏳ Đang tải danh sách...</div>
                    ) : (
                      <SubmissionsTable>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Học sinh (Lớp)</th>
                            <th>Đề thi</th>
                            <th>Điểm</th>
                            <th>Nộp lúc</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((sub: any, idx: number) => {
                            const { name, className } = getSubmissionNameAndClass(sub);
                            const maxScore = sub.exam?.total_marks ?? 100;
                            const isSelected = selectedSubmission?.id === sub.id;
                            return (
                              <tr
                                key={sub.id}
                                title={!sub.exam_id ? 'Đề thi đã bị xóa — không thể xem chi tiết' : undefined}
                                style={{
                                  cursor: sub.exam_id ? 'pointer' : 'not-allowed',
                                  opacity: sub.exam_id ? 1 : 0.45,
                                  background: isSelected ? '#eef2ff' : undefined,
                                  fontWeight: isSelected ? 600 : undefined,
                                }}
                                onClick={() => handleAllSubRowClick(sub)}
                              >
                                <td>{idx + 1}</td>
                                <td>
                                  {name}
                                  <span style={{ color: '#64748b', fontSize: 11, marginLeft: 4 }}>({className})</span>
                                </td>
                                <td style={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {sub.exam?.title || (sub.exam_id ? `Đề #${sub.exam_id}` : '⚠️ Đề (đã xóa)')}
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>{sub.total_score}/{maxScore}</td>
                                <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{formatDateTime(sub.finished_at)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </SubmissionsTable>
                    )}
                  </PanelBody>
                </Panel>

                {/* ─── RIGHT: detail panel (same logic as submissionsModal right panel) ─── */}
                <Panel>
                  <PanelHeader>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <div>
                        {submissionsLoading
                          ? '⏳ Đang tải chi tiết...'
                          : selectedSubmission
                            ? `Chi tiết: ${getSubmissionNameAndClass(selectedSubmission).name} (${getSubmissionNameAndClass(selectedSubmission).className}) — ${submissionsExam?.title || ''}`
                            : 'Chọn một bài nộp ở bên trái để xem chi tiết'}
                      </div>
                      {selectedSubmission && selectedSubmission._submissionCount && selectedSubmission._submissionCount > 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <label style={{ fontSize: '13px', color: '#475569', fontWeight: '500', whiteSpace: 'nowrap' }}>Chọn lần nộp:</label>
                          <SubmissionSelector
                            value={selectedSubmission?.id || ''}
                            onChange={(e) => {
                              const submissionId = parseInt(e.target.value);
                              const allSubs = selectedSubmissionForDetail?._allSubmissions || selectedSubmission._allSubmissions || [];
                              const submissionCount = selectedSubmissionForDetail?._submissionCount || selectedSubmission._submissionCount || 1;
                              const chosen = allSubs.find((s: any) => s.id === submissionId);
                              if (chosen) {
                                chosen._submissionCount = submissionCount;
                                chosen._allSubmissions = allSubs;
                                setSelectedSubmission(chosen);
                                setSelectedSubmissionForDetail(chosen);
                                setTimeout(() => { if (detailPanelBodyRef.current) detailPanelBodyRef.current.scrollTop = 0; }, 0);
                              }
                            }}
                          >
                            {(selectedSubmissionForDetail?._allSubmissions || selectedSubmission._allSubmissions || []).map((s: any, idx: number) => {
                              const totalCount = selectedSubmissionForDetail?._submissionCount || selectedSubmission._submissionCount || 1;
                              return (
                                <option key={s.id} value={s.id}>
                                  Lần {totalCount - idx} (Điểm: {s.total_score}/{submissionsExam?.total_marks ?? 100})
                                </option>
                              );
                            })}
                          </SubmissionSelector>
                        </div>
                      )}
                    </div>
                  </PanelHeader>
                  <PanelBody ref={detailPanelBodyRef}>
                    {submissionsLoading ? (
                      <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>⏳ Đang tải...</div>
                    ) : !selectedSubmission ? (
                      <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', fontSize: 15 }}>
                        👈 Click vào một bài nộp bên trái để xem chi tiết
                      </div>
                    ) : (() => {
                      const userAnswers = selectedSubmission.user_answers || [];
                      let totalCorrectSubQuestions = 0;
                      let totalWrongSubQuestions = 0;
                      userAnswers.forEach((ua: any) => {
                        if (ua.total_sub_questions && ua.total_sub_questions > 1) {
                          totalCorrectSubQuestions += ua.correct_count || 0;
                          totalWrongSubQuestions += (ua.total_sub_questions - (ua.correct_count || 0));
                        } else {
                          if (ua.is_correct === true || ua.is_correct === 1) totalCorrectSubQuestions += 1;
                          else totalWrongSubQuestions += 1;
                        }
                      });
                      const visibleAnswers = (() => {
                        if (answerFilter === 'correct') return userAnswers.filter((ua: any) => ua.total_sub_questions && ua.total_sub_questions > 1 ? (ua.correct_count || 0) > 0 : (ua.is_correct === true || ua.is_correct === 1));
                        if (answerFilter === 'wrong') return userAnswers.filter((ua: any) => ua.total_sub_questions && ua.total_sub_questions > 1 ? (ua.total_sub_questions - (ua.correct_count || 0)) > 0 : (ua.is_correct === false || ua.is_correct === 0));
                        return userAnswers;
                      })();
                      const grouped2: Record<number, any[]> = {};
                      visibleAnswers.forEach((ua: any) => {
                        const typeId = ua.question?.question_type_id || 0;
                        if (!grouped2[typeId]) grouped2[typeId] = [];
                        grouped2[typeId].push(ua);
                      });
                      const groups2 = Object.entries(grouped2).sort(([a], [b]) => parseInt(a) - parseInt(b)).map(([_, answers]) => answers.slice().sort((a: any, b: any) => (a.question?.order_index ?? 0) - (b.question?.order_index ?? 0)));
                      const qNumberMap2 = new Map<number, number>();
                      let counter2 = 1;
                      groups2.forEach((grp) => { grp.forEach((ua: any) => { if (ua.question?.id) { qNumberMap2.set(ua.question.id, counter2); counter2++; } }); });

                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                          <div style={{ display: 'flex', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                            <TagButton active={answerFilter === 'all'} onClick={() => setAnswerFilter('all')}>Tất cả ({totalCorrectSubQuestions + totalWrongSubQuestions})</TagButton>
                            <TagButton active={answerFilter === 'correct'} onClick={() => setAnswerFilter('correct')}>Đúng ({totalCorrectSubQuestions})</TagButton>
                            <TagButton active={answerFilter === 'wrong'} onClick={() => setAnswerFilter('wrong')}>Sai ({totalWrongSubQuestions})</TagButton>
                          </div>
                          <div style={{ display: 'flex', gap: 16, padding: '10px', background: '#f0f9ff', borderRadius: '8px', fontSize: '13px', borderLeft: '3px solid #3b82f6', flexWrap: 'wrap', alignItems: 'center' }}>
                            <div><span style={{ color: '#0f172a', fontWeight: '700', fontSize: '14px' }}>Điểm: </span><span style={{ fontWeight: '600' }}>{selectedSubmission?.total_score}{submissionsExam?.total_marks ? `/${submissionsExam.total_marks}` : ''}</span></div>
                            <div><span style={{ color: '#0f172a', fontWeight: '700', fontSize: '14px' }}>Bắt đầu: </span><span style={{ fontWeight: '600' }}>{selectedSubmission?.started_at ? formatDateTime(selectedSubmission.started_at) : '—'}</span></div>
                            <div><span style={{ color: '#0f172a', fontWeight: '700', fontSize: '14px' }}>Nộp: </span><span style={{ fontWeight: '600' }}>{selectedSubmission?.finished_at ? formatDateTime(selectedSubmission.finished_at) : '—'}</span></div>
                            <div><span style={{ color: '#0f172a', fontWeight: '700', fontSize: '14px' }}>Thời gian: </span><span style={{ fontWeight: '600' }}>{(() => { if (!selectedSubmission?.started_at || !selectedSubmission?.finished_at) return '—'; const ms = new Date(selectedSubmission.finished_at).getTime() - new Date(selectedSubmission.started_at).getTime(); const h = Math.floor(ms / 3600000); const m = Math.floor((ms % 3600000) / 60000); const s = Math.floor((ms % 60000) / 1000); return h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`; })()}</span></div>
                          </div>
                          {groups2.map((group, exerciseIdx) => {
                            const firstQ = group[0]?.question;
                            const typeInfo = firstQ?.questionType || {};
                            const headerText = `Exercise ${exerciseIdx + 1}: ${typeInfo.eng_title || ''}${typeInfo.vn_title ? ` (${typeInfo.vn_title})` : ''}`;
                            return (
                              <div key={`ex-${exerciseIdx}`}>
                                <h4 style={{ color: '#1d4ed8', fontSize: 16, fontWeight: 700, margin: '0 0 12px 0', borderBottom: '2px solid #bfdbfe', paddingBottom: 6 }}>{headerText}</h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                  {group.map((ua: any, idx: number) => {
                                    const q = ua.question || {};
                                    let content: any = q.content || {};
                                    if (typeof content === 'string') { try { content = JSON.parse(content); } catch { content = {}; } }
                                    const questionTypeId = q.question_type_id || q.questionType?.id;
                                    const correct = (() => {
                                      switch (questionTypeId) {
                                        case 1: if (content.options && content.correct_index !== undefined) { const lbl = String.fromCharCode(65 + content.correct_index); const txt = content.options[content.correct_index]; return txt ? `${lbl}. ${txt}` : lbl; } break;
                                        case 2: return content.correct_answer;
                                        case 3: if (content?.correct_answers?.length) return content.correct_answers.join(' | '); if (content?.correct_sentence) return content.correct_sentence; return null;
                                        case 4: if (Array.isArray(content.prompts)) { const ans: string[] = []; for (const p of content.prompts) { if (p.answers?.length) ans.push(p.answers.filter(Boolean).join(' / ')); else if (p.answer) ans.push(p.answer); } if (ans.length) return ans.join(' | '); } break;
                                        case 5: if (content?.correct_answers?.length) return content.correct_answers.join(' | '); if (content?.correct_answer) return content.correct_answer; return null;
                                        case 8: if (Array.isArray(content.answers)) return content.answers.join(', '); break;
                                        case 9: if (Array.isArray(content.questions)) return content.questions.map((qq: any, i: number) => { if (qq.options && qq.correct_index !== undefined) { const lbl = String.fromCharCode(65 + qq.correct_index); return `${i + 1}) ${lbl}. ${qq.options[qq.correct_index]}`; } return `${i + 1}) —`; }).join(' | '); break;
                                        default: break;
                                      }
                                      if (q.correctAnswers && q.correctAnswers[0]) return q.correctAnswers[0].answer;
                                      return null;
                                    })();
                                    const correctCandidates = shouldHighlightWordDiff(questionTypeId) && typeof correct === 'string' ? splitCorrectAnswerCandidates(correct) : [];
                                    const acceptedLabel = correctCandidates.length > 1 ? 'Đáp án đúng được chấp nhận:' : 'Đáp án đúng:';
                                    const renderAcceptedAnswers = () => correctCandidates.length > 1 ? <span style={{ display: 'inline-block' }}>{correctCandidates.map((a, i) => <div key={i}>{a}</div>)}</span> : correct;
                                    const isCorrect = ua.is_correct;
                                    const renderText = () => {
                                      const c = content || {};
                                      if (questionTypeId === 3 && Array.isArray(c.words)) return c.words.join(' / ');
                                      if (questionTypeId === 4 && c.prompts?.length > 0) return c.prompts[0].text;
                                      if ((questionTypeId === 6 || questionTypeId === 7 || questionTypeId === 9) && c.passage) { let bi = 1; return c.passage.replace(/_{3,}|\.{3,}/g, () => `(${bi++}) ____`); }
                                      if (questionTypeId === 8 && c.text) return c.text;
                                      if (questionTypeId === 11 && c.sentence) { const s = c.sentence; const u = c.underlined || ''; if (u && s.includes(u)) { const parts = s.split(u); return <><span>{parts[0]}</span><u style={{ fontWeight: 'bold' }}>{u}</u><span>{parts[1] || ''}</span><div style={{ marginTop: 4 }}>____________________</div></>; } return s; }
                                      return c.text || c.original_sentence || c.sentence || '';
                                    };
                                    const qNum = qNumberMap2.get(q.id) || idx + 1;
                                    const explanation = q?.explanation;
                                    const explanationOpen = q?.id ? showExplanationMap[q.id] : false;
                                    const toggleExplanation = () => { if (q?.id) setShowExplanationMap(prev => ({ ...prev, [q.id]: !prev[q.id] })); };
                                    return (
                                      <AnswerCard key={ua.id || idx}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <div style={{ fontWeight: 700 }}>
                                            {qNum}. [<a href={`/questions-manage?search=${q.id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline', fontWeight: 700 }}>{q.id}</a>] {renderText()}
                                          </div>
                                          {(() => {
                                            if (ua.total_sub_questions && ua.total_sub_questions > 1) {
                                              const cc = ua.correct_count || 0; const wc = ua.total_sub_questions - cc;
                                              return <div style={{ display: 'flex', gap: 4 }}>{cc > 0 && <Pill variant="success">Đúng({cc})</Pill>}{wc > 0 && <Pill variant="danger">Sai({wc})</Pill>}</div>;
                                            }
                                            return <Pill variant={isCorrect ? 'success' : 'danger'}>{isCorrect ? 'Đúng' : 'Sai'}</Pill>;
                                          })()}
                                        </div>
                                        <div style={{ marginTop: 6 }}>
                                          {questionTypeId === 12 && content?.passage && <div style={{ fontSize: 13, color: '#0f172a', marginBottom: 12, fontStyle: 'italic' }}>{content.passage}</div>}
                                          {questionTypeId === 12 && Array.isArray(content?.statements) && <div style={{ fontSize: 13, color: '#0f172a', marginBottom: 12 }}>{content.statements.map((st: any, i: number) => <div key={i}>{i + 1}) {st.text}</div>)}</div>}
                                          <div style={{ fontSize: 13, color: '#0f172a' }}>
                                            <strong>Câu trả lời:</strong> {(() => {
                                              if (questionTypeId === 12 && Array.isArray(content?.statements)) {
                                                const parts = ua.answer ? ua.answer.split(' | ').map((s: string) => s.trim()) : [];
                                                return <span>{content.statements.map((_: any, i: number) => { const a = parts[i] || '—'; const cv = content.statements[i]?.is_true ? 'T' : 'F'; const na = a.toUpperCase() === 'TRUE' ? 'T' : a.toUpperCase() === 'FALSE' ? 'F' : a.toUpperCase(); const ok = na === cv; const da = na === 'T' ? 'True' : na === 'F' ? 'False' : na; return <span key={i}>{i > 0 && '  -  '}<span style={{ color: ok ? '#65a30d' : '#dc2626', fontWeight: 600 }}>{i + 1}.{da}</span></span>; })}</span>;
                                              }
                                              if (questionTypeId === 9 && content?.questions && Array.isArray(content.questions)) {
                                                const parts = ua.answer ? ua.answer.split(' | ').map((s: string) => s.trim()) : [];
                                                return parts.map((a: string, i: number) => { const sq = content.questions[i]; if (sq?.options) { const oi = sq.options.findIndex((o: string) => o === a); if (oi !== -1) return `${i + 1}) ${String.fromCharCode(65 + oi)}. ${a}`; } return `${i + 1}) ${a}`; }).join(' | ');
                                              }
                                              if (questionTypeId === 10 && Array.isArray(content?.options)) { const ui = Number(ua.answer); if (!Number.isNaN(ui) && content.options[ui] !== undefined) return `${String.fromCharCode(65 + ui)}. ${content.options[ui]}`; }
                                              if (!isCorrect && shouldHighlightWordDiff(questionTypeId) && typeof ua.answer === 'string' && correctCandidates.length > 0) return highlightMismatchedWords(ua.answer, correctCandidates);
                                              return formatAnswer(ua.answer);
                                            })()}
                                          </div>
                                          <div style={{ fontSize: 13, color: '#475569', marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            <strong>{acceptedLabel}</strong>
                                            <span>{(() => {
                                              if (questionTypeId === 12 && Array.isArray(content?.statements)) return <span>{content.statements.map((st: any, i: number) => <span key={i}>{i > 0 && '  -  '}{i + 1}.{st.is_true ? 'True' : 'False'}</span>)}</span>;
                                              if (questionTypeId === 10 && Array.isArray(content?.options)) { const wi = content.wrong_index; const corr = content.correct_answer || content.correction || ''; if (wi !== undefined && content.options[wi] !== undefined) return `${String.fromCharCode(65 + wi)}. ${content.options[wi]} → ${corr}`; }
                                              if (questionTypeId === 11) { if (content?.correct_questions?.length) return content.correct_questions.join(' | '); if (content?.correct_question) return content.correct_question; }
                                              return renderAcceptedAnswers();
                                            })()}</span>
                                          </div>
                                          {explanation && (
                                            <div style={{ marginTop: 8 }}>
                                              <button type="button" onClick={toggleExplanation} style={{ background: '#e0f2fe', border: '1px solid #93c5fd', color: '#0f172a', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                                                {explanationOpen ? 'Ẩn giải thích' : 'Hiện giải thích'}
                                              </button>
                                              {explanationOpen && <div style={{ marginTop: 6, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>{explanation}</div>}
                                            </div>
                                          )}
                                          <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>Điểm: {ua.score}</div>
                                        </div>
                                      </AnswerCard>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </PanelBody>
                </Panel>

              </ModalContentGrid>
            </ModalBox>
          </Overlay>
        );
      })()}


    </Container>
  );
};

export default ExamsManage;
